import { useAtomValue, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { getSiteSetupToolCalls } from '@services/content-agents/browser';

import { useLoggedInUser } from '../../state';
import { agentIsSynced, type AgentStateSource, useAgentState } from '../Agent';

type SiteSetupStepStatuses = Partial<
    Record<api.SiteSetupStepId, api.AIToolCallSiteSetupStep['status']>
>;

/**
 * The setup steps of a site as its setup conversation last showed them. Kept in this browser for
 * the sidebar card, which reads the site's data and so cannot tell a skipped or started step apart.
 */
const siteSetupStepStatusesAtom = atomFamily((key: string) =>
    atomWithStorage<SiteSetupStepStatuses>(
        `siteSetupStepStatuses__${JSON.stringify(key)}`,
        {},
        undefined,
        { getOnInit: true }
    )
);

/** The setup steps of a site as its setup conversation last showed them. */
export function useSiteSetupStepStatuses(siteId: string): SiteSetupStepStatuses {
    const user = useLoggedInUser();
    return useAtomValue(siteSetupStepStatusesAtom(`${user.uid}:${siteId}`));
}

/**
 * Keep the step statuses of a site's setup conversation for {@link useSiteSetupStepStatuses}.
 * Only once the conversation is synced, so a client that has not received it yet does not erase
 * them.
 */
export function useRecordSiteSetupStepStatuses(props: {
    agent: AgentStateSource;
    siteId: string | undefined;
}): void {
    const { agent, siteId } = props;
    const user = useLoggedInUser();
    const setStatuses = useSetAtom(siteSetupStepStatusesAtom(`${user.uid}:${siteId ?? ''}`));
    const synced = useAgentState(agent, agentIsSynced);
    // Serialized, so the selector returns a value that only changes with the statuses.
    const statuses = useAgentState(agent, siteId ? selectSiteSetupStepStatuses : () => '{}');

    React.useEffect(() => {
        if (siteId && synced) {
            setStatuses(JSON.parse(statuses));
        }
    }, [siteId, synced, statuses, setStatuses]);
}

/** The latest status of each step in the conversation, serialized. */
function selectSiteSetupStepStatuses(state: { messages: api.AIMessage[] }): string {
    const latest: SiteSetupStepStatuses = {};
    for (const toolCall of getSiteSetupToolCalls(state.messages)) {
        latest[toolCall.step] = toolCall.status;
    }
    return JSON.stringify(latest);
}
